import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import { useIsPlatformAdmin } from "@/hooks/usePlatformSummary";
import { CompanySelectorModal } from "@/components/CompanySelectorModal";

interface ProtectedRouteProps {
  children: React.ReactNode;
  portalGuard?: "controller" | "worker" | "contract_labor" | "customer";
}

const Spinner = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, portalGuard }) => {
  const { user, loading } = useAuth();
  const {
    isPersonnel,
    isVendor,
    isContractLabor,
    isCustomer,
    userProfile,
    isLoading: permLoading,
    isError: permError,
    isFetched: permFetched,
  } = usePermissions();
  const { needsSelection, isLoading: companyLoading } = useCompanySelector();
  const { data: isPlatformAdmin, isLoading: platformLoading, isError: platformError } = useIsPlatformAdmin();

  // Still booting auth — wait.
  if (loading) return <Spinner />;

  // Truly signed out — only redirect when auth boot has completed and there's no user.
  if (!user) return <Navigate to="/auth" replace />;

  // Authenticated — wait for permission/company data before deciding.
  if (permLoading || companyLoading || platformLoading) return <Spinner />;

  // If profile lookup hit an error, do NOT bounce to /auth. Keep showing a spinner
  // while React Query retries; sending the user back to sign-in here is the
  // false-logout bug.
  if (permError || platformError) return <Spinner />;

  // No profile row resolved AND the query actually finished successfully.
  if (!userProfile && permFetched) {
    // Confirmed platform admin — send to /platform.
    if (isPlatformAdmin) return <Navigate to="/platform" replace />;
    // Genuine orphan account — only now is it safe to send to /auth.
    return <Navigate to="/auth" replace />;
  }

  // Show company picker if user belongs to multiple companies and hasn't chosen yet
  if (needsSelection) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <CompanySelectorModal open={true} onSelected={() => window.location.reload()} />
      </div>
    );
  }

  // Controller portal: block all portal-only roles
  if (portalGuard === "controller" && (isPersonnel || isVendor || isContractLabor || isCustomer)) {
    if (isCustomer) return <Navigate to="/portal/customer" replace />;
    if (isContractLabor) return <Navigate to="/portal/contract-labor" replace />;
    return <Navigate to="/portal/worker" replace />;
  }

  // Worker portal: personnel only
  if (portalGuard === "worker" && !isPersonnel) {
    return <Navigate to="/controller" replace />;
  }

  // Contract labor portal: contract_labor only
  if (portalGuard === "contract_labor" && !isContractLabor) {
    return <Navigate to="/controller" replace />;
  }

  // Customer portal: customer roles only
  if (portalGuard === "customer" && !isCustomer) {
    return <Navigate to="/controller" replace />;
  }

  return <>{children}</>;
};
